/**
 * Sidebar Logic - Pure Functions
 *
 * Pure functions for determining sidebar state based on timeslot selection.
 * Extracted from useSidebarIntegration hook for testability without
 * requiring React or service dependencies.
 *
 * @requirements 4.1, 4.2, 4.3 - Sidebar Integration
 */

import { TimeSlot } from '../types'

/**
 * Represents the possible sidebar states derived from the current selection.
 */
export type SidebarState =
  | { type: 'default' }
  | { type: 'eventManager'; timeSlots: TimeSlot[] }
  | { type: 'appointmentDetails'; registrationId: number }

/**
 * Pure function that determines the sidebar state based on the current selection.
 *
 * Requirement 4.1: When no timeslots are selected, show default calendar sidebar
 * Requirement 4.2: When one or more available timeslots are selected, show Event Manager
 * Requirement 4.3: When a single occupied timeslot is clicked, show Appointment Details
 *
 * @param selectedSlots - Array of currently selected timeslots
 * @returns The sidebar state that should be displayed
 */
export function determineSidebarState(selectedSlots: TimeSlot[]): SidebarState {
  if (selectedSlots.length === 0) {
    return { type: 'default' }
  }
  if (selectedSlots.length === 1 && selectedSlots[0].patientId) {
    return {
      type: 'appointmentDetails',
      registrationId: parseInt(selectedSlots[0].patientId, 10),
    }
  }
  return {
    type: 'eventManager',
    timeSlots: selectedSlots,
  }
}
